<reupload-modal>

    <dialog class="modal modal-middle" ref="dialog" aria-labelledby="reupload-modal-heading">
        <div class="modal-box max-w-6xl w-11/12 max-h-[90vh] overflow-y-auto">
            <form method="dialog">
                <button class="absolute end-2 top-2 cursor-pointer text-xl" aria-label="{locale?.t('close')}">
                    <i class="icon-clear"></i>
                </button>
            </form>

            <div class="flex items-center gap-2 mb-4 pe-8">
                <h3 id="reupload-modal-heading" class="font-bold text-lg text-primary">
                    {modalTitle()}
                </h3>
                <span class="flex-grow"></span>
                <upload-file-extensions-tooltip></upload-file-extensions-tooltip>
            </div>

            <virtual if="{state.open && state.resource}">
                <upload-source-picker
                        resourceid="{state.resource.resourceId}"
                        leftheading="{locale?.t('content_item_reuploadcontent')}"
                        rightheading="{locale?.t('reupload_reimportfromrepository')}"
                        disabled="{isUploadBlocked()}"
                        disabledreason="{getDisabledReason()}"
                        onhandoff="{hideDialog}"
                        onhandoffcancel="{reshow}"
                        oncomplete="{handleComplete}"></upload-source-picker>
            </virtual>

            <div class="modal-action">
                <form method="dialog">
                    <button class="btn text-primary rounded-lg"
                            aria-label="{locale?.t('cancel')}">{locale?.t('cancel')}</button>
                </form>
            </div>
        </div>
        <form method="dialog" class="modal-backdrop">
            <button aria-label="{locale?.t('close')}">close</button>
        </form>
    </dialog>

    <script>
        import uploadSourcePicker from './upload-source-picker.tag';
        import uploadFileExtensionsTooltip from './upload-file-extensions-tooltip.tag';

        export default {
            includeStore: true,

            get components() {
                return { uploadSourcePicker, uploadFileExtensionsTooltip };
            },

            state: {
                open: false,
                resource: null,
                quotaDetails: null,
                subscriptionStatus: null
            },

            onMounted() {
                // Global mount (index.tag): both the content item page and the
                // processing queue's job-failed flow open it via window.reuploadModal.
                window.reuploadModal = this;

                // Reset state whenever the native dialog closes (Cancel, X,
                // backdrop, Esc) so a later reupload of a *different* resource
                // starts clean. The handoff (see hideDialog) also closes the
                // dialog but must keep our state, so it's guarded by a flag.
                this.$('dialog').addEventListener('close', () => {
                    if (this._handingOff) {
                        this._handingOff = false;
                        return;
                    }
                    // show() is async (it awaits a quota refresh), so a prior
                    // close's 'close' event can land after show() has already
                    // reopened the dialog. If the dialog is open again, this
                    // event is stale - ignore it, or it would blank the freshly
                    // opened modal (dialog visible but state.open false).
                    if (this.$('dialog').open) return;
                    this._onComplete = null;
                    this.update({ open: false, resource: null });
                });

                // Mirror the Upload page's quota/subscription gating so an
                // expired subscription or exhausted quota disables the picker
                // here too. Both fields are seeded by app boot and refreshed via
                // tools.fetchAndUpdate* helpers, so we just track the store.
                this.unsubscribe = this.store.subscribe(() => {
                    const state = this.store.getState();
                    let changed = false;
                    if (state.quotaDetails !== this.state.quotaDetails) {
                        this.state.quotaDetails = state.quotaDetails;
                        changed = true;
                    }
                    if (state.subscriptionStatus !== this.state.subscriptionStatus) {
                        this.state.subscriptionStatus = state.subscriptionStatus;
                        changed = true;
                    }
                    if (changed) this.update();
                });
                const seeded = this.store.getState();
                this.state.quotaDetails = seeded.quotaDetails;
                this.state.subscriptionStatus = seeded.subscriptionStatus;
            },

            onBeforeUnmount() {
                if (this.unsubscribe) this.unsubscribe();
            },

            // Blocks the picker for the same reasons the Upload page does:
            // subscription can't upload, or credits exhausted.
            isUploadBlocked() {
                if (this.state.subscriptionStatus && !this.state.subscriptionStatus.canUploadResources) {
                    return true;
                }
                if (!this.state.quotaDetails) {
                    return false;
                }
                return this.state.quotaDetails.remainingCredits <= 0 ||
                       this.getUsedCredits() >= this.state.quotaDetails.totalCredits;
            },

            getUsedCredits() {
                if (!this.state.quotaDetails) {
                    return 0;
                }
                return (this.state.quotaDetails.totalCredits || 0) - (this.state.quotaDetails.remainingCredits || 0);
            },

            getDisabledReason() {
                if (this.state.subscriptionStatus && !this.state.subscriptionStatus.canUploadResources) {
                    if (this.state.subscriptionStatus.status === 'trial') {
                        return 'trial-expired';
                    }
                    if (this.state.subscriptionStatus.status === 'canceled') {
                        return 'grace-period';
                    }
                    return 'subscription-expired';
                }
                return 'quota-exhausted';
            },

            // "Reupload: <name>" once a resource is set; the heading is always
            // rendered for aria-labelledby, so fall back to the bare label.
            modalTitle() {
                const base = this.locale?.t('content_item_reuploadcontent') || '';
                const name = this.state.resource?.displayName;
                return name ? `${base}: ${name}` : base;
            },

            // resource: { resourceId, displayName } to reingest into.
            // onComplete: host callback fired after a successful reupload/reingest.
            async show(resource, onComplete) {
                // Refresh quota so the gate uses live credits; subscription
                // status is seeded at boot and tracked via the store.
                try {
                    await this.tools?.fetchAndUpdateQuota();
                } catch (e) {
                    // fall back to seeded state
                }
                // Pure quota exhaustion gets the dedicated upsell modal (nicer
                // than opening a disabled picker). A subscription block has no
                // such modal, so we open and let the picker render disabled with
                // the reason, exactly like the Upload page.
                if (this.isUploadBlocked() && this.getDisabledReason() === 'quota-exhausted') {
                    this.tools?.showQuotaLimitModal();
                    return;
                }
                this._onComplete = onComplete || null;
                this._handingOff = false;
                this.update({ open: true, resource });
                this.$('dialog').showModal();
            },

            // Step aside while a downstream modal (repository file picker, or the
            // pre-upload dialog) takes over, so they aren't stacked. The picker
            // stays mounted (open kept true) so the in-flight reupload/reingest
            // still completes and fires oncomplete; handleComplete then resets.
            hideDialog() {
                this._handingOff = true;
                this.$('dialog').close();
            },

            // The downstream repository file picker was cancelled without a
            // selection; bring the host modal back rather than leaving the user
            // with nothing open (the picker is still mounted from the handoff).
            reshow() {
                if (this.state.open) {
                    this._handingOff = false;
                    this.$('dialog').showModal();
                }
            },

            handleComplete() {
                const cb = this._onComplete;
                this._onComplete = null;
                this.update({ open: false, resource: null });
                if (cb) cb();
            }
        }
    </script>
</reupload-modal>
